/**
 * Lead sources — how the customer heard of the company (`docs/new-request.md`
 * §14.4, Board 12 D).
 *
 * A short list every company starts with six of — Google, Referral, Repeat
 * customer, Yard sign, Facebook, Vehicle — and anyone taking a request can add
 * one from the sheet, which is how *Nextdoor* turns up here. Most used first,
 * the order New request's list offers them in.
 *
 * Nothing is removed by switching one off: an inactive source is gone from the
 * form and stays on every job that already names it, which is why the switch
 * is on the row rather than behind a Remove.
 */
import { useEffect, useState } from 'react'
import type { MeResponse } from '@rowted/shared'
import { CatalogList } from '../components/catalog-list.js'
import { Switch } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import type { ListRow } from '../lib/use-list.js'
import { useSettingsBar } from './settings-bar.js'

const AREA = 'catalog-admin/lead-sources' as const

/** An inactive source reads faint across the row, as Radio does on the board. */
const off = (row: ListRow) => row.active === false

/**
 * The Active switch, on the row (Board 12 D).
 *
 * It writes through the same PUT the sheet does, with the version it reads
 * first, so a rename somebody saved a moment ago is not overwritten by a
 * switch that only meant to change one thing.
 */
function ActiveSwitch({ row, canEdit, onChanged, onProblem }: {
  row: ListRow
  canEdit: boolean
  onChanged: () => void
  onProblem: (message: string) => void
}) {
  const [on, setOn] = useState(!off(row))
  const [busy, setBusy] = useState(false)
  useEffect(() => setOn(!off(row)), [row.active])

  async function flip(next: boolean) {
    setOn(next)
    setBusy(true)
    try {
      const current = await request<{ name: string; version: number }>(`/${AREA}/${String(row.id)}`)
      await request(`/${AREA}/${String(row.id)}`, {
        method: 'PUT', body: { name: current.name, active: next, version: current.version },
      })
      onChanged()
    } catch (cause) {
      setOn(!next)
      onProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    /* The row opens the sheet; the switch is its own control and must not. */
    <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
      <Switch
        checked={on}
        onChange={(next) => void flip(next)}
        label={`${String(row.name ?? 'Lead source')} active`}
        hideLabel
        disabled={!canEdit || busy}
      />
    </span>
  )
}

export function LeadSourcesPage({ me }: { me: MeResponse }) {
  const [reloadKey, setReloadKey] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)
  const canEdit = me.permissions.company_settings === 'edit'
    || me.permissions.company_settings === 'full'
  const reload = () => setReloadKey((key) => key + 1)

  // The shell owns the page bar (`design-remediation.md` §1.1).
  useSettingsBar({
    title: 'Lead sources',
    sub: 'most used first · switch one off to hide it from the form',
  })

  return (
    <div className="p-4">
      <CatalogList
        area={AREA}
        title="Lead sources"
        primaryHeader="Lead source"
        singular="lead source"
        canEdit={canEdit}
        reloadKey={reloadKey}
        onChanged={reload}
        primary={(row) => ({
          primary: (
            <span className={cn(off(row) && 'font-normal text-text-faint')}>
              {String(row.name ?? '')}
            </span>
          ),
        })}
        cell={{
          jobsThisYear: (row) => (
            <span className={cn('tnum', off(row) ? 'text-text-faint' : 'text-text')}>
              {formatNumber(Number(row.jobsThisYear ?? 0))}
            </span>
          ),
          active: (row) => (
            <ActiveSwitch row={row} canEdit={canEdit} onChanged={reload} onProblem={setProblem} />
          ),
        }}
        blank={{ name: '', active: true }}
        fields={[{ name: 'name', label: 'Name', required: true }]}
        emptyTitle="No lead sources yet"
        emptyBody="Where the company’s work comes from — Google, a referral, a yard sign. New request asks for one on every job."
      />
      {problem && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setProblem(null)}>{problem}</Toast>
        </div>
      )}
    </div>
  )
}
